import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';

const defaultLabelSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.7rem',
  letterSpacing: '0.1em',
  fontWeight: 600,
  textTransform: 'uppercase',
  mb: 0.25,
};

const defaultValueSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: '0.95rem',
};

interface DescriptionFieldProps {
  label: string;
  value?: string | string[] | null;
  children?: React.ReactNode;
  preserveWhitespace?: boolean;
}

const normalizeValue = (value: DescriptionFieldProps['value']): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .join(', ');
  }
  return '';
};

export const DescriptionField: React.FC<DescriptionFieldProps> = ({
  label,
  value,
  children,
  preserveWhitespace = false,
}) => {
  const stringValue = normalizeValue(value);
  const hasValue = stringValue.trim() !== '';
  if (!hasValue && !children) return null;

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={defaultLabelSx}>
        {label}
      </Typography>
      {children ? (
        <Box sx={defaultValueSx}>{children}</Box>
      ) : (
        <Typography
          variant="body1"
          sx={preserveWhitespace ? { ...defaultValueSx, whiteSpace: 'pre-wrap' as const } : defaultValueSx}
        >
          {stringValue}
        </Typography>
      )}
    </Box>
  );
};
